/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

import { Fragment } from 'react';
import sortBy from 'lodash/sortBy';

import { Divider, Menu, NavLink, Stack } from '@mantine/core';

import {
  ActionDescriptor,
  ActionSignatureArgs,
  useResourceActions,
} from '@helpers/file';

import { palette } from '@ui-kit/colors';
import ShortcutKbd from '@ui-kit/atoms/ShortcutKbd';

import { ResourceAction } from '@app-types/blackroom-app';
import { ResourceContextMenuInternalProps } from '../types';
import { vars } from '@app/shared/themes/vars';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function InnerMenu({
  resources = [],
  allowedActions,
  onClickAction = () => {},
  renderWithoutTarget = false,
  innerMenuProps,
  actionOptions: options,
  resetRowSelection,
  requestVariables,
}: ResourceContextMenuInternalProps) {
  /* ********************************** HOOKS ********************************* */

  // Helpers
  const actions = useResourceActions();

  const filteredActions = Object.entries(actions).filter(([action]) =>
    allowedActions.includes(action as ResourceAction),
  ) as Array<[ResourceAction, ActionDescriptor]>;

  /* ***************************** LOCAL VARIABLES **************************** */

  const ItemComponent = renderWithoutTarget ? NavLink : Menu.Item;
  const DividerComponent = renderWithoutTarget ? Divider : Menu.Divider;
  const RootComponent = renderWithoutTarget ? Stack : Fragment;

  const navlinkProps = renderWithoutTarget
    ? {
        py: '0.625rem',
        styles: {
          root: { borderRadius: vars.radius.sm },
        },
      }
    : undefined;

  const resourceIds = resources.map((r) => r.id);
  const [singleResource] = resources;
  const singleResourceId = singleResource?.id;

  /* ******************************** RENDERING ******************************* */
  return (
    <RootComponent
      {...(renderWithoutTarget
        ? {
            gap: 0,
          }
        : {})}
      {...innerMenuProps}
    >
      {sortBy(filteredActions, 'rank').map(
        ([
          actionName,
          { iconComponent: Icon, label, withDivider, action, badge, shortcut },
        ]) => {
          const item = (
            <ItemComponent
              leftSection={<Icon size={16} />}
              color={actionName === 'delete' ? 'red' : undefined}
              onClick={async () => {
                await action({
                  resources: resources as ActionSignatureArgs['resources'],
                  singleResource:
                    singleResource as ActionSignatureArgs['singleResource'],
                  resourceIds,
                  singleResourceId,
                  options,
                  resetRowSelection,
                  requestVariables,
                });
                if (onClickAction) {
                  onClickAction(actionName);
                }
              }}
              rightSection={
                badge ??
                (shortcut ? <ShortcutKbd shortcut={shortcut} /> : <></>)
              }
              label={renderWithoutTarget ? label : undefined}
              {...((renderWithoutTarget ? { ...navlinkProps } : {}) as any)}
            >
              {label}
            </ItemComponent>
          );

          return (
            <div key={actionName}>
              {item}
              {withDivider ? (
                <DividerComponent
                  {...(renderWithoutTarget
                    ? { size: 'xs', color: palette.blackroomLightGray3 }
                    : {})}
                />
              ) : null}
            </div>
          );
        },
      )}
    </RootComponent>
  );
}

export default InnerMenu;
